import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import AxelAdminPanel from "./AxelAdminPanel";
import AxelSystemHealthPanel from "./AxelSystemHealthPanel";
import Mt5BridgeHealth from "./Mt5BridgeHealth";
import Mt5BridgeSummary from "./Mt5BridgeSummary";
import Mt5AuditLog from "./Mt5AuditLog";
import AxelRouterPanel from "./AxelRouterPanel";
import AdminsPanel from "./AdminsPanel";
import CohortPulse from "./CohortPulse";
import {
  B, btnStyle, HeaderBar, StatsBar, AdminTabs, Toast,
  NotificationsList, UserListSection, ProgressPanel, IntelLockControl, BulkIntelBar,
} from "./AdminDashboardParts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const COURSE_LABEL = { beginner: "Beginner", advanced: "Advanced", cfd: "CFD", commodities: "Commodities", roadmap: "Roadmap" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("users");
  const [notifs, setNotifs] = useState([]);
  const [toast, setToast] = useState(null);
  const [intelLock, setIntelLock] = useState(null);
  const [intelLockBusy, setIntelLockBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Quick filter — narrows the user list to a specific access subset.
  // Useful for auditing the paid list ("intel"), PMIR ("pmir"), or admins ("admin").
  const [accessFilter, setAccessFilter] = useState("all");
  // LLM-budget breaker status — polled every 60s. Lets the admin spot a
  // budget exhaustion at a glance via the red dot on the Total Users tile.
  const [budgetStatus, setBudgetStatus] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, ax);
      setUsers(data);
    } catch (err) { console.error("[AdminDashboard] fetchUsers:", err?.message || err); }
    setLoading(false);
  }, []);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications`, ax);
      setNotifs(data);
    } catch (err) { console.error("[AdminDashboard] fetchNotifs:", err?.message || err); }
  }, []);

  const fetchIntelLock = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/intel/lock`, ax);
      setIntelLock(data);
    } catch (err) { console.error("[AdminDashboard] fetchIntelLock:", err?.message || err); }
  }, []);

  const fetchBudgetStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/llm/budget-status`, ax);
      setBudgetStatus(data);
    } catch (err) { console.error("[AdminDashboard] fetchBudgetStatus:", err?.message || err); }
  }, []);

  useEffect(() => {
    fetchUsers(); fetchNotifs(); fetchIntelLock(); fetchBudgetStatus();
    // Poll budget status every 60s — cheap (single Mongo-free read) and
    // means an exhausted key surfaces on the dashboard within a minute.
    const id = setInterval(fetchBudgetStatus, 60_000);
    return () => clearInterval(id);
  }, [fetchUsers, fetchNotifs, fetchIntelLock, fetchBudgetStatus]);

  if (!user || user.role !== "admin") {
    return (
      <div style={{ minHeight: "100vh", background: B.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <p style={{ color: B.red, fontSize: 18 }}>Admin access required</p>
        <button onClick={() => navigate("/")} style={btnStyle}>Back to Home</button>
      </div>
    );
  }

  const filteredUsers = users.filter(u => {
    const matchSearch = u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.name?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    const isAdminU = u.role === "admin";
    // Admins always bypass PMIR + Intel gates — include them in "unlocked" filters.
    if (accessFilter === "intel") return !!u.intel_access || isAdminU;
    if (accessFilter === "pmir")  return !!u.pmir_access  || isAdminU;
    if (accessFilter === "admin") return isAdminU;
    if (accessFilter === "none")  return !u.intel_access && !u.pmir_access && !isAdminU;
    return true;
  });

  const filterCounts = {
    all:   users.length,
    intel: users.filter(u => u.intel_access || u.role === "admin").length,
    pmir:  users.filter(u => u.pmir_access  || u.role === "admin").length,
    admin: users.filter(u => u.role === "admin").length,
    none:  users.filter(u => !u.intel_access && !u.pmir_access && u.role !== "admin").length,
  };

  const flashToast = (msg, color) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 4000);
  };

  const toggleAccess = async (userId, course) => {
    const u = users.find(x => x._id === userId);
    if (!u) return;
    const current = u.course_access || [];
    const next = current.includes(course) ? current.filter(c => c !== course) : [...current, course];
    const wasGranted = !current.includes(course);
    try {
      await axios.put(`${API}/admin/users/${userId}/access`, { course_access: next }, ax);
      setUsers(prev => prev.map(x => x._id === userId ? { ...x, course_access: next } : x));
      const label = COURSE_LABEL[course] || course;
      flashToast(
        wasGranted ? `${u.name || u.email} granted ${label} access. Notification sent.`
                   : `${u.name || u.email} ${label} access revoked.`,
        wasGranted ? B.green : B.red,
      );
      fetchNotifs();
    } catch (err) { console.error("[AdminDashboard] toggleAccess:", err?.message || err); }
  };

  const togglePmirAccess = async (userId) => {
    const u = users.find(x => x._id === userId);
    if (!u) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/pmir`, {}, ax);
      setUsers(prev => prev.map(x => x._id === userId ? { ...x, pmir_access: data.pmir_access } : x));
      flashToast(
        `PMIR access ${data.pmir_access ? "granted to" : "revoked from"} ${u.name || u.email}.`,
        data.pmir_access ? B.green : B.red,
      );
    } catch (err) { console.error("[AdminDashboard] togglePmirAccess:", err?.message || err); }
  };

  const toggleIntelAccess = async (userId) => {
    const u = users.find(x => x._id === userId);
    if (!u) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/intel`, {}, ax);
      setUsers(prev => prev.map(x => x._id === userId ? { ...x, intel_access: data.intel_access } : x));
      flashToast(
        `Intel Hub access ${data.intel_access ? "granted to" : "revoked from"} ${u.name || u.email}.`,
        data.intel_access ? B.green : B.red,
      );
      fetchIntelLock();  // refresh unlocked_count
    } catch (err) { console.error("[AdminDashboard] toggleIntelAccess:", err?.message || err); }
  };

  const toggleQuantBeta = async (userId) => {
    const u = users.find(x => x._id === userId);
    if (!u) return;
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/quant-beta`, {}, ax);
      setUsers(prev => prev.map(x => x._id === userId ? { ...x, axel_quant_beta_enabled: data.axel_quant_beta_enabled } : x));
      flashToast(
        `AXEL Quant Beta ${data.axel_quant_beta_enabled ? "enabled for" : "disabled for"} ${u.name || u.email}.`,
        data.axel_quant_beta_enabled ? B.green : B.red,
      );
    } catch (err) { console.error("[AdminDashboard] toggleQuantBeta:", err?.message || err); }
  };

  const toggleGlobalIntelLock = async () => {
    if (!intelLock) return;
    const next = !intelLock.enabled;
    setIntelLockBusy(true);
    try {
      await axios.put(`${API}/admin/intel/lock`, { enabled: next }, ax);
      flashToast(
        next ? "Intel Hub LOCKED globally. Only admins + unlocked users can access /intel."
             : "Intel Hub UNLOCKED for all signed-in users.",
        next ? "#f472b6" : B.green,
      );
      fetchIntelLock();
    } catch (err) {
      console.error("[AdminDashboard] toggleGlobalIntelLock:", err?.message || err);
      flashToast("Failed to update Intel lock.", B.red);
    }
    setIntelLockBusy(false);
  };

  // ── Bulk INTEL selection + actions ──────────────────────────────────
  const selectionToggle = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const selectAllVisible = (visibleUsers, shouldSelect) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleUsers.forEach(u => { if (shouldSelect) next.add(u._id); else next.delete(u._id); });
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkIntel = async (grant) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/intel/bulk`, { user_ids: ids, grant }, ax);
      setUsers(prev => prev.map(u => ids.includes(u._id) ? { ...u, intel_access: grant } : u));
      flashToast(
        `Intel access ${grant ? "GRANTED to" : "REVOKED from"} ${data.updated} user${data.updated === 1 ? "" : "s"}.`,
        grant ? "#f472b6" : B.red,
      );
      fetchIntelLock();
      clearSelection();
    } catch (err) {
      console.error("[AdminDashboard] bulkIntel:", err?.message || err);
      flashToast("Bulk INTEL update failed.", B.red);
    }
    setBulkBusy(false);
  };

  const revokeSession = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/revoke-session`, {}, ax);
      alert("Session revoked. User will be logged out.");
    } catch (err) { console.error("[AdminDashboard] revokeSession:", err?.message || err); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Delete this user and all their progress?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, ax);
      setUsers(prev => prev.filter(x => x._id !== userId));
      if (selectedUser?.user?._id === userId) setSelectedUser(null);
    } catch (err) { console.error("[AdminDashboard] deleteUser:", err?.message || err); }
  };

  const viewProgress = async (userId) => {
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/progress`, ax);
      setSelectedUser(data);
    } catch (err) { console.error("[AdminDashboard] viewProgress:", err?.message || err); }
  };

  const userHandlers = {
    onSelectToggle: selectionToggle,
    onToggleAccess: toggleAccess,
    onTogglePmir: togglePmirAccess,
    onToggleIntel: toggleIntelAccess,
    onToggleQuantBeta: toggleQuantBeta,
    onView: viewProgress,
    onRevoke: revokeSession,
    onDelete: deleteUser,
  };

  const showSearchAndList = tab !== "notifications" && tab !== "axel" && tab !== "system" && tab !== "admins" && tab !== "bridge" && tab !== "router";

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: "100vh", background: B.bg, color: B.white, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <HeaderBar
        onAnalytics={() => navigate("/admin/analytics")}
        onHome={() => navigate("/")}
        onLogout={() => { logout(); navigate("/"); }}
      />

      <StatsBar users={users} budgetStatus={budgetStatus} />

      {/* Intel Hub global lock — visible to anyone with `users` admin perm */}
      {(user?.is_owner || (user?.admin_permissions || []).includes("users")) && (
        <IntelLockControl lockState={intelLock} onToggle={toggleGlobalIntelLock} busy={intelLockBusy} />
      )}

      {/* Cohort Pulse — 24h activity tile (owner or `cohort` perm) */}
      {(user?.is_owner || (user?.admin_permissions || []).includes("cohort")) && <CohortPulse />}

      <AdminTabs user={user} tab={tab} setTab={setTab}
        setSelectedUser={setSelectedUser} notifsCount={notifs.length} />

      <Toast toast={toast} />

      {/* Search + quick filter */}
      {showSearchAndList && (
        <div style={{ padding: "16px 24px 0" }}>
          <input data-testid="admin-search" placeholder="Search users by name or email..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", maxWidth: 500, padding: "10px 16px", borderRadius: 8, border: `1px solid ${B.border}`, background: B.card, color: B.white, fontSize: 14, outline: "none" }} />
          <div data-testid="access-filter-row" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
            {[
              { id: "all",   label: "All",            color: B.whiteGray },
              { id: "intel", label: "Intel unlocked", color: "#f472b6"   },
              { id: "pmir",  label: "PMIR unlocked",  color: "#38bdf8"   },
              { id: "admin", label: "Admins",         color: B.green     },
              { id: "none",  label: "Standard users", color: B.gray      },
            ].map(f => {
              const active = accessFilter === f.id;
              return (
                <button key={f.id} data-testid={`access-filter-${f.id}`}
                  onClick={() => setAccessFilter(f.id)}
                  style={{
                    padding: "5px 12px", borderRadius: 50, fontSize: 11, fontWeight: 700,
                    fontFamily: "monospace", letterSpacing: 0.6, cursor: "pointer",
                    background: active ? f.color + "22" : "transparent",
                    border: `1px solid ${active ? f.color : B.border}`,
                    color: active ? f.color : B.gray,
                  }}>
                  {f.label} <span style={{ opacity: 0.65, marginLeft: 4 }}>{filterCounts[f.id]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "axel"   && <AxelAdminPanel />}
      {tab === "router" && <AxelRouterPanel />}
      {tab === "system" && <AxelSystemHealthPanel />}
      {tab === "bridge" && <><Mt5BridgeSummary /><Mt5BridgeHealth /><Mt5AuditLog /></>}
      {tab === "admins" && <AdminsPanel />}
      {tab === "notifications" && <NotificationsList notifs={notifs} />}

      {showSearchAndList && (
        <div style={{ padding: "0 24px 40px", display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 600px" }}>
            <BulkIntelBar
              selectedIds={selectedIds}
              filteredUsers={filteredUsers}
              onGrant={() => bulkIntel(true)}
              onRevoke={() => bulkIntel(false)}
              onClear={clearSelection}
              onSelectAllVisible={(shouldSelect) => selectAllVisible(filteredUsers, shouldSelect)}
              busy={bulkBusy}
            />
            <UserListSection loading={loading} filteredUsers={filteredUsers}
              selectedUser={selectedUser} selectedIds={selectedIds} handlers={userHandlers} />
          </div>
          <ProgressPanel selectedUser={selectedUser} onClose={() => setSelectedUser(null)} />
        </div>
      )}
    </div>
  );
}
